<script setup lang="ts">
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useFetchPerSchemeTariffCodesRates } from "@/hooks/use-tariff-codes";
import { useMedicalSchemesStore } from '@/store/use-medical-schemes-store';
import { useTariffCodeStore } from '@/store/use-tariff-codes-store';
import {
  Activity,
  AlertCircle,
  ArrowLeft,
  Building2,
  DollarSign,
  Eye,
  FileText,
  Merge,
  RefreshCcwDot,
  Search,
  SunsetIcon,
  UploadIcon
} from 'lucide-vue-next';
import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';


// Get route to access params
const route = useRoute();

// Router
const router = useRouter();

// Get store
const tariffCodesStore = useTariffCodeStore();
const medicalSchemeStore = useMedicalSchemesStore();

// Destructure state with storeToRefs
const { medicalScheme } = storeToRefs(medicalSchemeStore)

const searchQuery = ref('');
const selectedFunder = ref('');
const selectedStatus = ref('');
const selectedYear = ref('');
const selectedRate = ref<any>(null);

// Props
const props = defineProps<{
  schemeId?: string
}>();

// Use schemeId from props or route params
const schemeId = computed(() => props.schemeId || route.params.schemeId as string);

// Pagination
const currentPage = ref(1);
const itemsPerPage = ref(15);

// Fetch tariff codes based on applicable year
const {
  data: fetchedData,
  isLoading, 
  isError, 
  error, 
  refetch 
} = useFetchPerSchemeTariffCodesRates(tariffCodesStore.getApplicableYear, schemeId.value)

// Use computed to reactively access the data
const tariffs = computed(() => fetchedData.value || [])

// Filtered tariffs
const filteredTariffs = computed(() => {
  let filtered = tariffs.value

  // Search filter
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    filtered = filtered.filter((tariff: any) =>
      tariff.tariff_code.code.toLowerCase().includes(query) ||
      tariff.tariff_code.code_description.toLowerCase().includes(query) ||
      tariff.funder.name.toLowerCase().includes(query) ||
      tariff.funder.scheme_code.toLowerCase().includes(query)
    )
  }

  // Funder filter
  if (selectedFunder.value && selectedFunder.value !== 'all') {
    filtered = filtered.filter((tariff: any) => tariff.funder.name === selectedFunder.value)
  }

  // Status filter
  if (selectedStatus.value && selectedStatus.value !== 'all') {
    const isActive = selectedStatus.value === 'active'
    filtered = filtered.filter((tariff: any) => tariff.active === isActive)
  }

  // Year filter
  if (selectedYear.value && selectedYear.value !== 'all') {
    filtered = filtered.filter((tariff: any) => tariff.applicable_year.toString() === selectedYear.value)
  }

  return filtered
})

// Unique funders
const uniqueFunders = computed(() => {
  const funders = tariffs.value.map((t: any) => t.funder.name)
  return [...new Set(funders)]
})

// Unique years
const uniqueYears = computed(() => {
  const years = tariffs.value.map((t: any) => t.applicable_year.toString())
  return [...new Set(years)].sort((a, b) => parseInt(b) - parseInt(a))
})

// Stats
const stats = computed(() => ({
  total: filteredTariffs.value.length,
  active: filteredTariffs.value.filter((t: any) => t.active).length,
  avgRate: filteredTariffs.value.length > 0
    ? (filteredTariffs.value.reduce((sum: number, t: any) => sum + t.recommendated_rates, 0) / filteredTariffs.value.length).toFixed(2)
    : '0.00',
  funders: uniqueFunders.value.length
}))

// Pagination
const totalPages = computed(() => {
  return Math.ceil(filteredTariffs.value.length / itemsPerPage.value)
})

const startIndex = computed(() => {
  return (currentPage.value - 1) * itemsPerPage.value
})

const endIndex = computed(() => {
  return startIndex.value + itemsPerPage.value
})

const paginatedTariffs = computed(() => {
  return filteredTariffs.value.slice(startIndex.value, endIndex.value)
})

const displayPages = computed(() => {
  const pages = []
  const maxPagesToShow = 5
  let startPage = Math.max(1, currentPage.value - Math.floor(maxPagesToShow / 2))
  let endPage = Math.min(totalPages.value, startPage + maxPagesToShow - 1)

  if (endPage - startPage < maxPagesToShow - 1) {
    startPage = Math.max(1, endPage - maxPagesToShow + 1)
  }

  for (let i = startPage; i <= endPage; i++) {
    pages.push(i)
  }

  return pages
})

// Computed property for safe access
const hasResults = computed(() => fetchedData.value !== null && fetchedData.value?.length > 0)

// Format currency
const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
  }).format(amount)
}

// Format date
const formatDate = (dateStr: string) => {
  return new Date(dateStr).toLocaleDateString('en-ZA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

// Get status color
const getStatusColor = (active: boolean) => {
  return active ? 'bg-green-500' : 'bg-gray-400'
}

const getBenefitClass = (code: string) => {
  const classes: Record<string, string> = {
    'SO': 'bg-green-100 text-green-800 border-green-200',
    'SI': 'bg-yellow-100 text-yellow-800 border-yellow-200',
    'RO': 'bg-blue-100 text-blue-800 border-blue-200',
    'RI': 'bg-purple-100 text-purple-800 border-purple-200'
  }
  return classes[code] || 'bg-gray-100 text-gray-800 border-gray-200'
}

const resetFilters = () => {
  searchQuery.value = ''
  selectedFunder.value = 'all'
  selectedStatus.value = 'all'
  selectedYear.value = 'all'
  currentPage.value = 1
}

const viewDetails = (tariff: any) => {
  selectedRate.value = tariff
}

const previousPage = () => {
  if (currentPage.value > 1) {
    currentPage.value--
  }
}

const nextPage = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++
  }
}

// Handlers
const handleSyncPracticeTypes = () => {
  console.log(`Navigate to Syncing Page`);
  router.push({name: 'SyncPracticesView'});
}

const handleSyncSunset = () => {
  console.log(`Navigate to Syncing Page`);
    router.push({name: 'SunsetTariffCodesView'});
}

const handleMergeHBCTariffCodes = () => {
  console.log(`Navigate to Merge HBC Tariff Codes Page`);
    router.push({name: 'MergeHBCTariffCodesView'});
}

const handleUploadTelephonicCodes = () => {
  console.log(`Navigate to Upload Telephonic Codes Page`);
    router.push({name: 'UploadTelephonicCodesView'});
}

const handleUpdateAllowedQty = () => {
  console.log(`Navigate to Update Allowed Qty Page`);
    router.push({name: 'UpdateAllowedQtyView'});
}
// Optional: Debug watch to see when data changes
watch(fetchedData, (newData) => {
  console.log('Fetched data changed:', newData)
})
</script>

<template>
  <div class="space-y-6 p-6">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <div>
        <h1 class="text-3xl font-bold text-gray-900">{{ medicalScheme?.name }} Tariff Rates Management</h1>
        <p class="text-gray-600 mt-1">Manage and view tariff codes, rates, and practice types</p>
      </div>
      <div>
        <Button
          @click="router.back()"
          class="flex items-center border border-gray-300 bg-gray-200 hover:bg-sky-700 hover:text-white"
        >
          <ArrowLeft/> Back
        </Button>
      </div> 
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="flex items-center justify-center py-12">
      <div class="text-center">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-600 mx-auto mb-4"></div>
        <p class="text-gray-600">Loading Schemes tariff rates...</p>
      </div>
    </div>

    <!-- Error State -->
    <Card v-else-if="isError" class="border-red-200 bg-red-50">
      <CardContent class="flex flex-col items-center justify-center py-12">
        <AlertCircle class="h-12 w-12 text-red-600 mb-4" />
        <p class="text-red-600 font-semibold mb-2">Error loading tariff codes</p>
        <p class="text-red-500 text-sm">{{ error?.message }}</p>
        <Button 
          @click="refetch()" 
          variant="outline"
          class="mt-4 border-red-600 text-red-600 hover:bg-red-600 hover:text-white"
        >
          Retry
        </Button>
      </CardContent>
    </Card>

    <!-- Main Content -->
    <template v-else-if="tariffs.length > 0">
      <!-- Stats Cards -->
      <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent class="pt-6">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm font-medium text-gray-600">Total Rates</p>
                <p class="text-3xl font-bold text-gray-900 mt-2">{{ stats.total }}</p>
              </div>
              <div class="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <FileText class="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent class="pt-6">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm font-medium text-gray-600">Active Rates</p>
                <p class="text-3xl font-bold text-green-600 mt-2">{{ stats.active }}</p>
              </div>
              <div class="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <Activity class="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent class="pt-6">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm font-medium text-gray-600">Avg. Rate</p>
                <p class="text-3xl font-bold text-gray-900 mt-2">R{{ stats.avgRate }}</p>
              </div>
              <div class="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <DollarSign class="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent class="pt-6">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm font-medium text-gray-600">Funders</p>
                <p class="text-3xl font-bold text-gray-900 mt-2">{{ stats.funders }}</p>
              </div>
              <div class="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <Building2 class="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

       <!-- Action Buttons -->
      <div class="flex w-full items-center justify-between border-t pt-4">
        <div>
          <h1 class="text-xl">Tariff Rates</h1>
        </div>
        <div class="flex items-center py-1 gap-3 bg-white rounded-md px-3">
          <Button
            @click="handleSyncPracticeTypes"
            class="flex text-white bg-sky-800 gap-1 p-1 border  border-sky-800 hover:bg-sky-700 hover:text-white"
          >
          <RefreshCcwDot class="h-4 w-4"/> Sync Practices
          </Button>
          <Button 
            @click="handleUploadTelephonicCodes"
            variant="outline" class="gap-1 text-white border-sky-700 bg-sky-800 hover:bg-sky-700 hover:text-white">
            <UploadIcon class="h-4 w-4" />
            Upload Telephonic Codes
          </Button>
          <Button 
            @click="handleUpdateAllowedQty"
            variant="outline" class="gap-1 text-white border-sky-700 bg-sky-800 hover:bg-sky-700 hover:text-white">
            <UploadIcon class="h-4 w-4" />
              Update Allowed Qty
          </Button>
          <Button
            @click="handleMergeHBCTariffCodes" 
            variant="outline" class="gap-1 text-white border-sky-700 bg-sky-800 hover:bg-sky-700 hover:text-white">
            <Merge class="h-4 w-4" />
            Merge HBC Codes
          </Button>
          <Button
            @click="handleSyncSunset"
            class="flex text-white bg-sky-800 gap-1 p-1 border border-sky-800 hover:bg-sky-700 hover:text-white"
          >
            <SunsetIcon class="h-4 w-4"/> Sunset Tariff Codes
          </Button>
          
        </div>
      </div>
      
      <!-- Table -->
      <Card>
        <CardContent class="p-0">
          <!-- Filters -->
          <div class="grid grid-cols-1 md:grid-cols-5 gap-4 border-b p-4">
            <div class="relative">
              <Search class="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                v-model="searchQuery"
                placeholder="Search tariff codes..."
                class="pl-9"
              />
            </div>

            <Select v-model="selectedFunder">
              <SelectTrigger>
                <SelectValue placeholder="All Funders" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Funders</SelectItem>
                <SelectItem v-for="funder in uniqueFunders" :key="funder" :value="funder">
                  {{ funder }}
                </SelectItem>
              </SelectContent>
            </Select>

            <Select v-model="selectedYear">
              <SelectTrigger>
                <SelectValue placeholder="All Years" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Years</SelectItem>
                <SelectItem v-for="year in uniqueYears" :key="year" :value="year">
                  {{ year }}
                </SelectItem>
              </SelectContent>
            </Select>

            <Select v-model="selectedStatus">
              <SelectTrigger>
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>

            <Button @click="resetFilters" variant="outline" class="w-full">
              Reset Filters
            </Button>
          </div>
      
          <div class="overflow-x-auto">
            <table class="w-full">
              <thead class="bg-gray-50 border-b">
                <tr>
                  <th class="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase">Tariff Code</th>
                  <th class="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase">Funder</th>
                  <th class="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase">Benefit Types</th>
                  <th class="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase">Rate</th>
                  <th class="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase">Limits</th>
                  <th class="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase">Year</th>
                  <th class="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase">Status</th>
                  <th class="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-200">
                <tr
                  v-for="tariff in paginatedTariffs"
                  :key="tariff.rate_id"
                  class="hover:bg-gray-50 transition-colors"
                >
                  <!-- Tariff Code -->
                  <td class="px-6 py-4">
                    <div class="max-w-xs">
                      <div class="font-semibold text-gray-900">{{ tariff.tariff_code.code }}</div>
                      <div class="text-sm text-gray-600 line-clamp-2">{{ tariff.tariff_code.code_description }}</div>
                      <Badge v-if="tariff.tariff_code.is_hbc" variant="default" class="mt-1">HBC</Badge>
                    </div>
                  </td>

                  <!-- Funder -->
                  <td class="px-6 py-4">
                    <div>
                      <div class="font-medium text-gray-900">{{ tariff.funder.name }}</div>
                      <div class="text-sm text-gray-600">{{ tariff.funder.scheme_code }}</div>
                    </div>
                  </td>

                  <!-- Benefit Types -->
                  <td class="px-6 py-4">
                    <div class="flex flex-wrap gap-1 max-w-xs">
                      <Badge
                        v-for="benefit in tariff.tariff_code.benefit_type.slice(0, 2)"
                        :key="benefit.id"
                        variant="outline"
                        :class="getBenefitClass(benefit.code)"
                        class="text-sm"
                      >
                        {{ benefit.code }}
                      </Badge>
                      <Badge
                        v-if="tariff.tariff_code.benefit_type.length > 2"
                        variant="outline"
                        class="text-sm bg-gray-100 text-gray-700"
                      >
                        +{{ tariff.tariff_code.benefit_type.length - 2 }}
                      </Badge>
                    </div>
                  </td>

                  <!-- Rate -->
                  <td class="px-6 py-4">
                    <div>
                      <div class="text-lg font-bold text-gray-900">{{ formatCurrency(tariff.recommendated_rates) }}</div>
                      <div class="text-sm text-gray-600">{{ tariff.billing_type.code }}</div>
                    </div>
                  </td>

                  <!-- Limits -->
                  <td class="px-6 py-4">
                    <div>
                      <div class="font-medium text-gray-900">{{ tariff.applicable_limits }}</div>
                      <div class="text-sm text-gray-600">{{ tariff.limit_type.code }}</div>
                    </div>
                  </td>

                  <!-- Year -->
                  <td class="px-6 py-4">
                    <Badge variant="secondary">{{ tariff.applicable_year }}</Badge>
                  </td>

                  <!-- Status -->
                  <td class="px-6 py-4">
                    <div class="flex items-center gap-2">
                      <div :class="['w-2 h-2 rounded-full', getStatusColor(tariff.active)]"></div>
                      <span class="text-sm font-medium">{{ tariff.active ? 'Active' : 'Inactive' }}</span>
                    </div>
                  </td>

                  <!-- Actions -->
                  <td class="px-6 py-4">
                    <Dialog>
                      <DialogTrigger as-child>
                        <Button
                          class="flex border gap-1 items-center border-yellow-500"
                          variant="ghost" 
                          size="sm"
                          @click="viewDetails(tariff)"
                        >
                          <Eye class="h-4 w-4" /> Details
                        </Button>
                      </DialogTrigger>
                      <DialogContent class="w-[70vw] h-[70vh] max-w-none sm:max-w-none bg-white border-yellow-400 overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle>Tariff Rate Details</DialogTitle>
                          <DialogDescription>
                            Complete information for {{ selectedRate?.tariff_code?.code }}
                          </DialogDescription>
                        </DialogHeader>

                        <div v-if="selectedRate" class="space-y-6 mt-4">
                          <!-- Tariff Code Info -->
                          <div>
                            <h4 class="text-lg font-semibold mb-3">Tariff Code Information</h4>
                            <div class="grid grid-cols-2 gap-4 bg-gray-50 rounded-lg p-4">
                              <div>
                                <p class="text-sm text-gray-600">Code</p>
                                <p class="font-semibold text-gray-900">{{ selectedRate.tariff_code.code }}</p>
                              </div>
                              <div>
                                <p class="text-sm text-gray-600">Description</p>
                                <p class="font-semibold text-gray-900">{{ selectedRate.tariff_code.code_description }}</p>
                              </div>
                              <div>
                                <p class="text-sm text-gray-600">Rate</p>
                                <p class="text-xl font-bold text-blue-600">{{ formatCurrency(selectedRate.recommendated_rates) }}</p>
                              </div>
                              <div>
                                <p class="text-sm text-gray-600">HBC</p>
                                <p class="font-semibold text-gray-900">{{ selectedRate.tariff_code.is_hbc ? 'Yes' : 'No' }}</p>
                              </div>
                            </div>
                          </div>

                          <!-- Benefit Types -->
                          <div>
                            <h4 class="text-lg font-semibold mb-3">Benefit Types</h4>
                            <div class="grid grid-cols-2 gap-3">
                              <div
                                v-for="benefit in selectedRate.tariff_code.benefit_type"
                                :key="benefit.id"
                                class="p-3 border rounded-lg"
                              >
                                <p class="font-semibold text-gray-900">{{ benefit.code }}</p>
                                <p class="text-sm text-gray-600">{{ benefit.code_description }}</p>
                              </div>
                            </div>
                          </div>

                          <!-- Practice Types -->
                          <div>
                            <h4 class="text-lg font-semibold mb-3">
                              Practice Types ({{ selectedRate.practice_type.length }})
                            </h4>
                            <div class="max-h-64 overflow-y-auto border rounded-lg">
                              <table class="w-full">
                                <thead class="bg-gray-50 sticky top-0">
                                  <tr>
                                    <th class="px-4 py-2 text-left text-sm font-semibold text-gray-700">Code</th>
                                    <th class="px-4 py-2 text-left text-sm font-semibold text-gray-700">Description</th>
                                  </tr>
                                </thead>
                                <tbody class="divide-y">
                                  <tr v-for="practice in selectedRate.practice_type" :key="practice.code">
                                    <td class="px-4 py-2 text-sm font-medium">{{ practice.code }}</td>
                                    <td class="px-4 py-2 text-sm text-gray-600">{{ practice.code_description }}</td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </div>

                          <!-- Additional Details -->
                          <div>
                            <h4 class="text-lg font-semibold mb-3">Additional Details</h4>
                            <div class="grid grid-cols-2 gap-4">
                              <div class="p-3 bg-gray-50 rounded-lg">
                                <p class="text-sm text-gray-600">Billing Type</p>
                                <p class="font-semibold">{{ selectedRate.billing_type.code_description }}</p>
                              </div>
                              <div class="p-3 bg-gray-50 rounded-lg">
                                <p class="text-sm text-gray-600">Frequency</p>
                                <p class="font-semibold">{{ selectedRate.frequency.code_description }}</p>
                              </div>
                              <div class="p-3 bg-gray-50 rounded-lg">
                                <p class="text-sm text-gray-600">Limit Type</p>
                                <p class="font-semibold">{{ selectedRate.limit_type.code_description }}</p>
                              </div>
                              <div class="p-3 bg-gray-50 rounded-lg">
                                <p class="text-sm text-gray-600">Applicable Limits</p>
                                <p class="font-semibold">{{ selectedRate.applicable_limits }}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </td>
                </tr>
              </tbody>
            </table>

            <!-- Empty State -->
            <div v-if="paginatedTariffs.length === 0" class="text-center py-12">
              <AlertCircle class="h-16 w-16 mx-auto text-gray-400 mb-4" />
              <p class="text-gray-600 text-lg font-medium">No tariff rates found</p>
              <p class="text-gray-500 text-sm mt-1">Try adjusting your filters</p>
            </div>
          </div>

          <!-- Pagination -->
          <div v-if="filteredTariffs.length > 0" class="bg-gray-50 px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div class="text-sm text-gray-700">
              Showing <span class="font-medium">{{ startIndex + 1 }}</span> to
              <span class="font-medium">{{ Math.min(endIndex, filteredTariffs.length) }}</span> of
              <span class="font-medium">{{ filteredTariffs.length }}</span> results
            </div>
            <div class="flex gap-2">
              <Button
                @click="previousPage"
                :disabled="currentPage === 1"
                variant="outline"
                size="sm"
              >
                Previous
              </Button>
              <Button
                v-for="page in displayPages"
                :key="page"
                @click="currentPage = page"
                :variant="currentPage === page ? 'default' : 'outline'"
                size="sm"
              >
                {{ page }}
              </Button>
              <Button
                @click="nextPage"
                :disabled="currentPage === totalPages"
                variant="outline"
                size="sm"
              >
                Next
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </template>

    <!-- Empty State -->
    <Card v-else>
      <CardContent class="flex flex-col items-center justify-center py-12">
        <AlertCircle class="h-12 w-12 text-gray-400 mb-4" />
        <p class="text-gray-500">No tariff data available</p>
        <Button 
          @click="refetch()" 
          variant="outline"
          class="mt-4"
        >
          Refresh
        </Button>
      </CardContent>
    </Card>
  </div>
</template>